import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { RelativeDirection } from '@/lib/filters/relative-dates';

export type DateMode = 'absolute' | 'relative';
export type { RelativeDirection };

export interface DateFilterInputProps {
  mode: DateMode;
  onModeChange: (mode: DateMode) => void;
  absoluteValue?: string;
  onAbsoluteValueChange: (value: string) => void;
  relativeValue?: number;
  onRelativeValueChange: (value: number) => void;
  relativeDirection?: RelativeDirection;
  onRelativeDirectionChange: (direction: RelativeDirection) => void;
  isBetween?: boolean;
  absoluteValueEnd?: string;
  onAbsoluteValueEndChange?: (value: string) => void;
  relativeValueEnd?: number;
  onRelativeValueEndChange?: (value: number) => void;
  relativeDirectionEnd?: RelativeDirection;
  onRelativeDirectionEndChange?: (direction: RelativeDirection) => void;
  /**
   * True once the user has clicked "Apply Filters" at least once on the
   * parent form. Used to gate inline validation — we don't want errors
   * flashing on initial render.
   */
  applyAttempted?: boolean;
}

export function DateFilterInput({
  mode,
  onModeChange,
  absoluteValue,
  onAbsoluteValueChange,
  relativeValue,
  onRelativeValueChange,
  relativeDirection = 'ago',
  onRelativeDirectionChange,
  isBetween = false,
  absoluteValueEnd,
  onAbsoluteValueEndChange,
  relativeValueEnd,
  onRelativeValueEndChange,
  relativeDirectionEnd = 'ago',
  onRelativeDirectionEndChange,
  applyAttempted = false,
}: DateFilterInputProps) {
  // Track first blur of the end-value input so we can surface "Required for
  // 'between'" once the user has interacted with that specific field, even
  // before they click Apply.
  const [endBlurred, setEndBlurred] = useState(false);
  const showEndError =
    isBetween &&
    (applyAttempted || endBlurred) &&
    (mode === 'absolute'
      ? !absoluteValueEnd
      : relativeValueEnd == null);
  const toDatetimeLocalValue = (isoString?: string): string => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  const fromDatetimeLocalValue = (localValue: string): string => {
    if (!localValue) return '';
    try {
      return new Date(localValue).toISOString();
    } catch {
      return '';
    }
  };

  const [localAbsoluteValue, setLocalAbsoluteValue] = useState(toDatetimeLocalValue(absoluteValue));
  const [localAbsoluteValueEnd, setLocalAbsoluteValueEnd] = useState(toDatetimeLocalValue(absoluteValueEnd));

  useEffect(() => {
    setLocalAbsoluteValue(toDatetimeLocalValue(absoluteValue));
  }, [absoluteValue]);

  useEffect(() => {
    setLocalAbsoluteValueEnd(toDatetimeLocalValue(absoluteValueEnd));
  }, [absoluteValueEnd]);

  const handleAbsoluteChange = (value: string) => {
    setLocalAbsoluteValue(value);
    onAbsoluteValueChange(fromDatetimeLocalValue(value));
  };

  const handleAbsoluteEndChange = (value: string) => {
    setLocalAbsoluteValueEnd(value);
    onAbsoluteValueEndChange?.(fromDatetimeLocalValue(value));
  };

  const handleRelativeValueChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      onRelativeValueChange(numValue);
    }
  };

  const handleRelativeValueEndChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      onRelativeValueEndChange?.(numValue);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-1">
      {/* Compact mode toggle */}
      <div className="flex rounded-md border overflow-hidden shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 px-2 text-xs rounded-none border-0",
            mode === 'absolute' && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
          )}
          onClick={() => onModeChange('absolute')}
        >
          Abs
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 px-2 text-xs rounded-none border-0 border-l",
            mode === 'relative' && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
          )}
          onClick={() => onModeChange('relative')}
        >
          Rel
        </Button>
      </div>

      {/* Value inputs */}
      {mode === 'absolute' ? (
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <Input
            type="datetime-local"
            value={localAbsoluteValue}
            onChange={(e) => handleAbsoluteChange(e.target.value)}
            className="flex-1 min-w-0 h-8 text-xs"
          />
          {isBetween && (
            <>
              <span className="text-xs text-muted-foreground shrink-0">to</span>
              <div className="flex-1 min-w-0">
                <Input
                  type="datetime-local"
                  value={localAbsoluteValueEnd}
                  onChange={(e) => handleAbsoluteEndChange(e.target.value)}
                  onBlur={() => setEndBlurred(true)}
                  aria-invalid={showEndError || undefined}
                  className={cn(
                    'min-w-0 h-8 text-xs w-full',
                    showEndError && 'border-destructive',
                  )}
                />
                {showEndError && (
                  <p className="mt-0.5 text-[10px] text-destructive">Required for 'between'</p>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <Input
            type="number"
            min="0"
            value={relativeValue ?? ''}
            onChange={(e) => handleRelativeValueChange(e.target.value)}
            placeholder="0"
            className="w-16 h-8 text-xs shrink-0"
          />
          <span className="text-xs text-muted-foreground shrink-0">days</span>
          <Select value={relativeDirection} onValueChange={onRelativeDirectionChange}>
            <SelectTrigger className="w-[90px] h-8 text-xs shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ago">ago</SelectItem>
              <SelectItem value="from_now">from now</SelectItem>
            </SelectContent>
          </Select>
          {isBetween && (
            <>
              <span className="text-xs text-muted-foreground shrink-0">to</span>
              <div className="flex flex-col items-start shrink-0">
                <Input
                  type="number"
                  min="0"
                  value={relativeValueEnd ?? ''}
                  onChange={(e) => handleRelativeValueEndChange(e.target.value)}
                  onBlur={() => setEndBlurred(true)}
                  placeholder="0"
                  aria-invalid={showEndError || undefined}
                  className={cn(
                    'w-16 h-8 text-xs',
                    showEndError && 'border-destructive',
                  )}
                />
                {showEndError && (
                  <p className="mt-0.5 text-[10px] text-destructive">Required for 'between'</p>
                )}
              </div>
              <span className="text-xs text-muted-foreground shrink-0">days</span>
              <Select
                value={relativeDirectionEnd}
                onValueChange={(value) => onRelativeDirectionEndChange?.(value as RelativeDirection)}
              >
                <SelectTrigger className="w-[90px] h-8 text-xs shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ago">ago</SelectItem>
                  <SelectItem value="from_now">from now</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      )}
    </div>
  );
}
